import type { SiteTaskCategory } from '@/lib/types';
import { SUBCATEGORY_NEEDS_COMPANY } from '@/lib/types';

/**
 * 현장 업무의 `content` 인코딩.
 *
 * v1은 컬럼을 늘리는 대신 분류별 입력값을 JSON 문자열로 만들어 `content` 한 칸에 밀어 넣었다.
 * 그 결과 `JSON.parse(content)` 가 화면 코드 곳곳에 흩어졌고, 표시·편집·대시보드·인쇄가
 * 저마다 조금씩 다르게 해석했다 — 예를 들어 대시보드는 `startDate`·`cumul` 을 아예 읽지 않는다.
 *
 * v2는 규칙을 이 파일 하나로 모은다. 스키마는 v1 그대로 두었다 —
 * 형태를 바꾸면 기존 데이터를 옮겨야 하고, 그 판단은 백엔드 스키마를 정할 때 함께 해야 한다.
 *
 * 인코딩은 세 가지다.
 * - `공정율`            → `{ plan, actual, comment, officeSync }`
 * - 하위 구분이 있는 분류 → `{ sub, text, company?, startDate?, endDate?, cumul? }`
 * - 그 밖               → 평문
 */

export type ProgressContent = {
  /** 계획(%). 입력 그대로의 문자열이다 — 빈 값과 0을 구분해야 한다. */
  plan: string;
  /** 실적(%) */
  actual: string;
  comment: string;
  /**
   * 이 값을 <b>공무 표의 실적공정률로 반영</b>할 것인가(사용자 확정 2026-08-13).
   *
   * 공무의 실적공정률은 원래 사람이 적는 칸이라, 켠 줄만 넘어간다. 옛 회의록에는 이 칸이 없고
   * 그때는 꺼진 것으로 본다 — 없던 동의를 있는 것으로 읽지 않는다.
   */
  officeSync: boolean;
};

export type DetailContent = {
  sub?: string;
  company?: string;
  text: string;
  startDate?: string;
  endDate?: string;
  /** 대갑기성 · 수금의 누계 금액. */
  cumul?: string;
};

function parseObject(content: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(content);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** 공정율 항목을 읽는다. JSON 이 아니면 `null` — 그때는 평문으로 보여준다. */
export function parseProgress(content: string): ProgressContent | null {
  const parsed = parseObject(content);
  if (!parsed || (!('plan' in parsed) && !('actual' in parsed))) return null;

  return {
    plan: str(parsed.plan),
    actual: str(parsed.actual),
    comment: str(parsed.comment),
    officeSync: parsed.officeSync === true,
  };
}

/** 하위 구분이 있는 분류를 읽는다. JSON 이 아니면 전체를 본문으로 본다. */
export function parseDetail(content: string): DetailContent {
  const parsed = parseObject(content);
  if (!parsed || !('text' in parsed)) return { text: content };

  return {
    sub: str(parsed.sub) || undefined,
    company: str(parsed.company) || undefined,
    text: str(parsed.text),
    startDate: str(parsed.startDate) || undefined,
    endDate: str(parsed.endDate) || undefined,
    cumul: str(parsed.cumul) || undefined,
  };
}

export function buildProgress(progress: ProgressContent): string {
  return JSON.stringify(progress);
}

/**
 * 하위 구분 항목을 만든다. 빈 값은 넣지 않는다 —
 * 넣어 두면 `parseDetail` 이 빈 문자열을 값으로 착각해 화면에 빈 대괄호가 뜬다.
 */
export function buildDetail(input: { category: SiteTaskCategory } & DetailContent): string {
  const payload: Record<string, string> = {
    sub: input.sub ?? '',
    text: input.text?.trim() ?? '',
  };

  if (input.sub && SUBCATEGORY_NEEDS_COMPANY.includes(input.sub) && input.company?.trim()) {
    payload.company = input.company.trim();
  }

  if (input.category === '공사진행사항') {
    if (input.startDate) payload.startDate = input.startDate;
    if (input.endDate) payload.endDate = input.endDate;
  }

  if (input.category === '대갑기성' && input.sub === '수금' && input.cumul?.trim()) {
    payload.cumul = input.cumul.trim();
  }

  return JSON.stringify(payload);
}

/** 하위 구분 항목에 저장할 내용이 하나라도 있는가. 전부 비었으면 추가를 막는다. */
export function hasDetailInput(input: DetailContent): boolean {
  return Boolean(
    input.text?.trim() || input.company?.trim() || input.cumul?.trim() || input.startDate || input.endDate,
  );
}

/** 대시보드·목록에서 한 줄로 줄여 보여줄 때. */
export function summarizeContent(category: SiteTaskCategory, content: string): string {
  if (category === '공정율') {
    const progress = parseProgress(content);
    if (!progress) return content;

    const numbers = [
      progress.plan && `계획 ${progress.plan}%`,
      progress.actual && `실적 ${progress.actual}%`,
    ].filter(Boolean).join(' / ');

    // 공무로 넘어가는 줄인지 목록에서 바로 보이게 한다 — 열어 봐야 알면 늦다.
    return [numbers, progress.officeSync ? '→공무' : '', progress.comment].filter(Boolean).join(' ');
  }

  const detail = parseDetail(content);
  if (!detail.sub) return detail.text;

  const label = detail.company ? `${detail.sub}-${detail.company}` : detail.sub;
  return detail.text ? `[${label}] ${detail.text}` : `[${label}]`;
}
